import { type User, onIdTokenChanged } from 'firebase/auth'
import { useEffect, useState } from 'react'
import { auth } from '~/src/lib/firebase/firebaseAuthInstance'

const CHECK_EVERY_MS = 20_000
/**
 * Renew this long before expiry. Wider than a hidden tab's timer gap (about a
 * minute once throttled), so a background tab still renews in time.
 */
const RENEW_LEAD_MS = 5 * 60 * 1000

/**
 * When Firebase will treat the token as expired, on this device's clock: it
 * stores issue time + lifetime locally, so a wrong device clock can't make a
 * fresh token look due. Undefined if the persisted shape ever changes.
 */
function localExpiry(user: User): number | undefined {
  const json = user.toJSON() as {
    stsTokenManager?: { expirationTime?: unknown }
  }
  const t = json.stsTokenManager?.expirationTime
  return typeof t === 'number' ? t : undefined
}

type LiveToken = { uid: string; token: string }

/**
 * The signed-in user's ID token, kept current for as long as the page is open.
 *
 * ⛔ `authUser.accessToken` is the token from page load. Firebase renews it
 * only when someone asks, and a token lives an hour, so headers built from it
 * go stale and the api stops trusting them. This checks on a short interval
 * and when the tab comes back (timers stall while a laptop sleeps), renewing
 * near expiry as judged on this device's clock, so a wrong clock can't loop
 * renewals and a failed one just retries on the next check. Undefined until known, or
 * when signed out; carries the uid so a caller never pairs one user's token
 * with another's id.
 */
export function useLiveIdToken(): LiveToken | undefined {
  const [live, setLive] = useState<LiveToken | undefined>()

  useEffect(() => {
    let cancelled = false

    const check = () => {
      const user = auth.currentUser
      if (!user) return
      const expiry = localExpiry(user)
      const due = expiry !== undefined && expiry - Date.now() < RENEW_LEAD_MS
      // Forced only when due; otherwise Firebase answers from memory.
      void user.getIdToken(due).catch(() => {})
    }

    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      if (!user) {
        setLive(undefined)
        return
      }
      try {
        const token = await user.getIdToken()
        if (cancelled || auth.currentUser?.uid !== user.uid) return
        setLive((prev) =>
          prev?.uid === user.uid && prev.token === token
            ? prev
            : { uid: user.uid, token }
        )
      } catch {
        // Leave the last token; the next check retries.
      }
    })

    const interval = setInterval(check, CHECK_EVERY_MS)
    const onWake = () => {
      if (document.visibilityState === 'visible') check()
    }
    document.addEventListener('visibilitychange', onWake)
    window.addEventListener('online', onWake)

    return () => {
      cancelled = true
      clearInterval(interval)
      unsubscribe()
      document.removeEventListener('visibilitychange', onWake)
      window.removeEventListener('online', onWake)
    }
  }, [])

  return live
}
